// The console's tester debug tool, ported from the buyer SPA's red "Debug"
// toggle (frontend/web debug-note-panel.tsx): a sidebar toggle flips debug mode
// ON, and while it is on this floating box shows over EVERY admin page so a
// tester can jot down what feels wrong from any screen. Submit posts the note
// (with an auto-captured html2canvas screenshot of what they were looking at)
// to POST /api/admin/debug-notes; notes land in the same "Debug notes" review
// tab as buyer-SPA notes, marked [admin] and attributed to the tester's name.
//
// The name is required (attribution is the whole point), folded into the note
// text by composeAdminDebugNote, and remembered in localStorage so a tester
// types it once. Withheld entirely in prod (config IS_PROD; the server 404s
// there too).
import { useState } from "react";
import {
  applyHomeEndKey,
  Button,
  Input,
  isCoarsePointer,
  shouldSendOnEnter,
} from "@recruiter/shared";

import { composeAdminDebugNote, readTesterName, writeTesterName } from "../lib/debug-note";
import "./debug-note-panel.css";

function localStore(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

type Status =
  | { kind: "idle" }
  | { kind: "saving" }
  | { kind: "saved" }
  | { kind: "error"; message: string };

export type DebugNotePanelProps = {
  // Posts the composed note text (`name: ...` / `note: [admin] ...`). Resolves
  // on success, throws on failure (surfaced inline).
  onSubmit: (note: string) => Promise<void>;
  // Leave debug mode (mirrors the sidebar toggle).
  onTurnOff: () => void;
};

export function DebugNotePanel({ onSubmit, onTurnOff }: DebugNotePanelProps) {
  const [name, setName] = useState(() => readTesterName(localStore()));
  const [note, setNote] = useState("");
  const [status, setStatus] = useState<Status>({ kind: "idle" });

  const saving = status.kind === "saving";
  // Both the name (attribution is the whole point) and the note are required.
  const canSend = name.trim().length > 0 && note.trim().length > 0 && !saving;

  async function send() {
    const who = name.trim();
    const text = note.trim();
    if (!who || !text || saving) return;
    setStatus({ kind: "saving" });
    try {
      // Fold the name + the [admin] marker into the note text, one string in the DB.
      await onSubmit(composeAdminDebugNote(who, text));
      // Clear the note but KEEP the name: a tester usually logs several notes.
      setNote("");
      setStatus({ kind: "saved" });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Could not save the note.";
      setStatus({ kind: "error", message });
    }
  }

  function clearOutcome() {
    if (status.kind !== "idle" && status.kind !== "saving") setStatus({ kind: "idle" });
  }

  return (
    <div className="debug-note-panel" role="region" aria-label="Debug note">
      <div className="debug-note-panel__head">
        <span className="debug-note-panel__dot" aria-hidden="true" />
        <span className="debug-note-panel__title">Debug note</span>
        <button
          type="button"
          className="debug-note-panel__off"
          onClick={onTurnOff}
          title="Turn off debug mode"
        >
          Turn off
        </button>
      </div>
      <p className="debug-note-panel__hint">
        Describe what feels wrong on this page. A screenshot of what you see is attached
        automatically.
      </p>
      <Input
        aria-label="Your name"
        placeholder="Your name"
        value={name}
        disabled={saving}
        onChange={(event) => {
          setName(event.target.value);
          writeTesterName(localStore(), event.target.value);
          clearOutcome();
        }}
      />
      <textarea
        className="debug-note-panel__input"
        aria-label="Describe what's wrong"
        placeholder="What looks broken or off?"
        rows={3}
        value={note}
        disabled={saving}
        onChange={(event) => {
          setNote(event.target.value);
          clearOutcome();
        }}
        onKeyDown={(event) => {
          // Enter sends; Shift+Enter inserts a newline (a note may span lines).
          // On touch there is no Shift, so Enter always inserts a newline
          // (same rule as the buyer SPA's panel, via the shared helper).
          if (shouldSendOnEnter(event, isCoarsePointer())) {
            event.preventDefault();
            void send();
          }
          applyHomeEndKey(event);
        }}
      />
      <div className="debug-note-panel__foot">
        <span
          className={`debug-note-panel__status debug-note-panel__status--${status.kind}`}
          role="status"
        >
          {status.kind === "saved"
            ? "Logged. Thank you!"
            : status.kind === "error"
              ? status.message
              : ""}
        </span>
        <Button
          variant="solid"
          tone="accent"
          size="sm"
          disabled={!canSend}
          onClick={() => void send()}
        >
          {saving ? "Logging..." : "Log note"}
        </Button>
      </div>
    </div>
  );
}
